import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getAllCities, getAllStates } from '../../services/locationService';
import { Field, inputCls } from './orgFormUtils';

function filterStates(states, query) {
  const q = query.trim().toLowerCase();
  if (!q) return states;
  return states.filter(
    (s) =>
      s.state_name.toLowerCase().includes(q)
      || s.state_code.toLowerCase().includes(q),
  );
}

function filterCities(cities, query) {
  const q = query.trim().toLowerCase();
  if (!q) return cities;
  return cities.filter((c) => c.city.toLowerCase().includes(q));
}

function SearchDropdown({
  label,
  required,
  query,
  onQueryChange,
  onSelect,
  onBlur,
  disabled,
  placeholder,
  open,
  onOpen,
  loading,
  options,
  getOptionKey,
  getOptionLabel,
  emptyText,
  error,
  inputName,
}) {
  const wrapRef = useRef(null);

  const handleBlur = () => {
    window.setTimeout(() => {
      if (wrapRef.current?.contains(document.activeElement)) return;
      onBlur();
    }, 150);
  };

  const fieldInputCls = error
    ? `${inputCls} border-red-400 dark:border-red-500 focus:border-red-500 focus:ring-red-500`
    : inputCls;

  return (
    <Field label={required ? `${label} *` : label}>
      <div ref={wrapRef} className="relative">
        <input
          type="text"
          name={inputName}
          className={fieldInputCls}
          value={query}
          onChange={(e) => {
            onQueryChange(e.target.value);
            onOpen(true);
          }}
          onFocus={() => onOpen(true)}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          data-lpignore="true"
          data-form-type="other"
        />
        {open && !disabled && (
          <ul className="absolute z-20 mt-1 w-full max-h-44 overflow-y-auto rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg">
            {loading ? (
              <li className="px-3.5 py-2.5 text-sm text-gray-500 dark:text-slate-400">
                Loading...
              </li>
            ) : options.length === 0 ? (
              <li className="px-3.5 py-2.5 text-sm text-gray-500 dark:text-slate-400">
                {emptyText}
              </li>
            ) : (
              options.map((option) => (
                <li key={getOptionKey(option)}>
                  <button
                    type="button"
                    className="w-full text-left px-3.5 py-2.5 text-sm text-gray-900 dark:text-white hover:bg-indigo-50 dark:hover:bg-indigo-600/20"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onSelect(option)}
                  >
                    {getOptionLabel(option)}
                  </button>
                </li>
              ))
            )}
          </ul>
        )}
        {error && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>
        )}
      </div>
    </Field>
  );
}

const StateCitySelect = forwardRef(function StateCitySelect(
  {
    stateCode,
    city,
    onStateChange,
    onCityChange,
    stateRequired = false,
    cityRequired = false,
    inputNamePrefix = 'athenian-location',
  },
  ref,
) {
  const [allStates, setAllStates] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [stateQuery, setStateQuery] = useState('');
  const [cityQuery, setCityQuery] = useState('');
  const [stateOpen, setStateOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [loadingStates, setLoadingStates] = useState(true);
  const [loadingCities, setLoadingCities] = useState(false);
  const [stateError, setStateError] = useState('');
  const [cityError, setCityError] = useState('');

  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;

  useImperativeHandle(ref, () => ({
    validate() {
      let valid = true;
      if (stateRequired && !stateCode) {
        setStateError('Please select a state from the dropdown');
        valid = false;
      }
      if (cityRequired && !city) {
        setCityError('Please select a city from the dropdown');
        valid = false;
      }
      return valid;
    },
  }));

  useEffect(() => {
    getAllStates()
      .then((res) => setAllStates(res.data?.data || []))
      .catch(() => setAllStates([]))
      .finally(() => setLoadingStates(false));
  }, []);

  useEffect(() => {
    if (!stateCode) {
      setStateQuery('');
      return;
    }
    const match = allStates.find((s) => s.state_code === stateCode)
      || allStates.find((s) => s.state_name.toLowerCase() === String(stateCode).toLowerCase());
    if (match) {
      setStateQuery(match.state_name);
      if (match.state_code !== stateCode) {
        onStateChangeRef.current(match.state_code);
      }
    }
  }, [stateCode, allStates]);

  useEffect(() => {
    setCityQuery(city || '');
  }, [city]);

  useEffect(() => {
    if (!stateCode) {
      setAllCities([]);
      return undefined;
    }
    setLoadingCities(true);
    getAllCities(stateCode)
      .then((res) => setAllCities(res.data?.data || []))
      .catch(() => setAllCities([]))
      .finally(() => setLoadingCities(false));
  }, [stateCode]);

  const stateOptions = useMemo(() => {
    if (!stateOpen) return [];
    return filterStates(allStates, stateQuery);
  }, [allStates, stateOpen, stateQuery]);

  const cityOptions = useMemo(() => {
    if (!cityOpen || !stateCode) return [];
    return filterCities(allCities, cityQuery);
  }, [allCities, cityOpen, cityQuery, stateCode]);

  const handleStateSelect = (state) => {
    onStateChange(state.state_code);
    setStateQuery(state.state_name);
    setStateOpen(false);
    setStateError('');
    onCityChange('');
    setCityQuery('');
    setCityError('');
  };

  const handleCitySelect = (option) => {
    onCityChange(option.city);
    setCityQuery(option.city);
    setCityOpen(false);
    setCityError('');
  };

  const handleStateBlur = () => {
    setStateOpen(false);
    if (stateCode) {
      const match = allStates.find((s) => s.state_code === stateCode);
      if (match) setStateQuery(match.state_name);
      setStateError('');
      return;
    }
    setStateQuery('');
    if (stateQuery.trim()) {
      setStateError('Select a valid state from the dropdown');
    } else if (stateRequired) {
      setStateError('State is required');
    } else {
      setStateError('');
    }
  };

  const handleCityBlur = () => {
    setCityOpen(false);
    if (city) {
      setCityQuery(city);
      setCityError('');
      return;
    }
    setCityQuery('');
    if (cityQuery.trim()) {
      setCityError('Select a valid city from the dropdown');
    } else if (cityRequired) {
      setCityError('City is required');
    } else {
      setCityError('');
    }
  };

  return (
    <>
      <SearchDropdown
        label="State"
        required={stateRequired}
        query={stateQuery}
        onQueryChange={(value) => {
          setStateQuery(value);
          onStateChange('');
          onCityChange('');
          setCityQuery('');
          setStateError('');
          setCityError('');
        }}
        onSelect={handleStateSelect}
        onBlur={handleStateBlur}
        disabled={loadingStates && allStates.length === 0}
        placeholder="Search and select state..."
        open={stateOpen}
        onOpen={setStateOpen}
        loading={loadingStates && allStates.length === 0}
        options={stateOptions}
        getOptionKey={(s) => s.state_code}
        getOptionLabel={(s) => s.state_name}
        emptyText="No states found — try another search"
        error={stateError}
        inputName={`${inputNamePrefix}-state`}
      />
      <SearchDropdown
        label="City"
        required={cityRequired}
        query={cityQuery}
        onQueryChange={(value) => {
          setCityQuery(value);
          onCityChange('');
          setCityError('');
        }}
        onSelect={handleCitySelect}
        onBlur={handleCityBlur}
        disabled={!stateCode}
        placeholder={!stateCode ? 'Select a state first' : 'Search and select city...'}
        open={cityOpen}
        onOpen={setCityOpen}
        loading={loadingCities && allCities.length === 0}
        options={cityOptions}
        getOptionKey={(c) => c.city}
        getOptionLabel={(c) => c.city}
        emptyText="No cities found — try another search"
        error={cityError}
        inputName={`${inputNamePrefix}-city`}
      />
    </>
  );
});

export default StateCitySelect;
